import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Minus, ScanLine, ShoppingCart, User, X, CreditCard, Camera, Printer, Lock, Unlock, Search, QrCode, Clock, LayoutGrid } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { useSystemSettings, useFeatureEnabled } from "../lib/systemSettings";
import { usePosLock } from "../lib/posLock";
import AdminAuthModal from "../components/AdminAuthModal";
import { Input } from "../components/ui/input";
import BarcodeScanner, { CARD_SCAN_FORMATS } from "../components/BarcodeScanner";
import PrintableReceipt from "../components/PrintableReceipt";

function formatCents(cents) {
  return `€ ${(cents / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Zahlungs-Marken-Logos (UX-Fix Paket C — 03.09.2026)
// ---------------------------------------------------------------------------
// Inline-SVG damit KEIN externer Marken-Asset geladen werden muss (kein
// CSP-Loch, kein Font-Flash). Formen sind bewusst schlicht (nicht pixel-
// genau), damit sie sich in die LOOP-POS-Ästhetik einfügen. Farben orientieren
// sich an den offiziellen Brand-Guidelines.
const PaypalLogo = () => (
  <span className="italic font-black text-[26px] leading-none tracking-tight">
    <span className="text-[#003087]">Pay</span>
    <span className="text-[#009cde]">Pal</span>
  </span>
);
const AppleGlyph = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);
const ApplePayLogo = ({ dark = true, size = 22 }) => (
  <span className={`inline-flex items-center gap-0.5 font-semibold ${dark ? "text-white" : "text-black"}`}>
    <AppleGlyph size={size} />
    <span style={{ fontSize: `${size}px`, lineHeight: 1 }}>Pay</span>
  </span>
);
const GoogleGlyph = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path fill="#4285F4" d="M21.35 11.1H12v2.85h5.35c-.24 1.4-1.02 2.6-2.2 3.4l3.55 2.75c2.07-1.9 3.25-4.7 3.25-7.9 0-.75-.07-1.5-.2-2.1z" />
    <path fill="#34A853" d="M12 22c2.95 0 5.45-.97 7.25-2.65l-3.55-2.75c-.99.66-2.27 1.06-3.7 1.06-2.85 0-5.25-1.92-6.1-4.5H2.24v2.83C4.05 19.55 7.75 22 12 22z" />
    <path fill="#FBBC04" d="M5.9 13.16c-.22-.66-.34-1.36-.34-2.16s.12-1.5.34-2.16V5.99H2.24C1.44 7.5 1 9.2 1 11s.44 3.5 1.24 5l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.5c1.6 0 3.05.55 4.2 1.63l3.15-3.15C17.45 2.24 14.95 1 12 1 7.75 1 4.05 3.45 2.24 6l3.66 2.84C6.75 6.26 9.15 5.5 12 5.5z" />
  </svg>
);
const GooglePayLogo = ({ size = 22 }) => (
  <span className="inline-flex items-center gap-1 font-medium text-zinc-900">
    <GoogleGlyph size={size} />
    <span style={{ fontSize: `${size}px`, lineHeight: 1 }}>Pay</span>
  </span>
);

// Runder Marken-Button (Zahlungsart-Auswahl).
function BrandCircle({ testid, onClick, disabled, bg, ring, label, logo, comingSoon }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="group flex flex-col items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-full disabled:cursor-not-allowed"
    >
      <span
        className={`relative w-20 h-20 sm:w-24 sm:h-24 rounded-full ${bg} ${ring} flex items-center justify-center shadow-lg ring-1 transition-transform group-enabled:group-hover:scale-105 group-enabled:group-active:scale-95 group-disabled:opacity-50`}
      >
        {logo}
        {comingSoon && (
          <span className="absolute -bottom-1 right-0 text-[8px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-full px-1.5 py-0.5">
            bald
          </span>
        )}
      </span>
      <span className="text-xs sm:text-sm text-zinc-300 group-disabled:text-zinc-500">
        {label}
      </span>
    </button>
  );
}

export default function POS() {
  const { brandName, settings } = useSystemSettings();
  const { isLocked: lockActive, lock: activateLock, unlock: releaseLock } = usePosLock();
  // Owner Review 2026-08-31 — Club Card = Kundenkonto-Infrastruktur, IMMER
  // aktiv. Nur das optionale Punkteprogramm ist noch feature-flagged.
  // Karten-Pflicht hängt jetzt ausschließlich am POS-Setting und am
  // Order-/Customer-Zustand.
  const pointsOn = useFeatureEnabled("points");
  const cardRequired =
    (settings?.pos_customer_card_required || "").toLowerCase() === "mandatory";
  const [lockModalOpen, setLockModalOpen] = useState(false);
  // Owner-Fix (05.09.2026): Entsperren-Flow lebt jetzt hier neben dem
  // KASSE-Header. Der frühere floating Button in Layout.jsx wurde
  // entfernt — es gibt exakt einen Lock/Unlock-Control.
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [terminalId, setTerminalId] = useState("");
  const [order, setOrder] = useState(null);
  const [scanValue, setScanValue] = useState("");
  const [customer, setCustomer] = useState(null);
  const [cardScan, setCardScan] = useState("");
  const [showCardModal, setShowCardModal] = useState(false);
  const [providers, setProviders] = useState(["mock"]);
  const [processing, setProcessing] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Owner Review 2026-08-31 — separater Kamera-Flow für Club-Card-QR.
  // Bewusst getrennt vom Produkt-Barcode-Scanner: nutzt dieselbe
  // BarcodeScanner-Komponente, aber mit QR_CODE-Formats-Whitelist,
  // damit ein Produkt-Barcode niemals einen Kundenlookup auslöst und
  // umgekehrt.
  const [cardCameraOpen, setCardCameraOpen] = useState(false);
  // PayPal-Approval-Flow: sobald checkout('paypal') eine approve_url liefert,
  // steht der Kassenbon-Screen still, bis der Kassierer „Zahlung prüfen"
  // (Capture) klickt oder abbricht. Order bleibt PENDING in der DB, kein
  // Ledger-Insert, kein Bestandsabzug.
  const [paypalPending, setPaypalPending] = useState(null);
  // POS Realbetrieb Hardening – Paket C (03.09.2026): QR-Handoff.
  // Wenn der Kassierer „PayPal per QR" wählt, blenden wir statt der
  // klassischen Redirect-Pane einen QR-Code der bestehenden approve_url
  // ein. Ein separates Polling-useEffect (siehe unten) beobachtet den
  // PayPal-Status serverseitig und finalisiert automatisch, sobald
  // PayPal APPROVED / COMPLETED meldet. Persistiert in localStorage
  // damit ein Reload denselben QR wieder anzeigt und keinen zweiten
  // PayPal-Intent erzeugt (Paket B-Recovery).
  const [paypalQRMode, setPaypalQRMode] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Owner-Fix (05.09.2026) — In-App-Confirm-Dialogs statt nativer
  // window.confirm(). Rest der POS-UI bleibt ruhig und konsistent, der
  // Kunde sieht keine Browser-Systemmeldung.
  const [cartCancelConfirmOpen, setCartCancelConfirmOpen] = useState(false);
  const [paypalAbortConfirmOpen, setPaypalAbortConfirmOpen] = useState(false);
  // POS Final UX (04.09.2026)
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [walletEligibility, setWalletEligibility] = useState({
    apple_pay: { eligible: false, reason: null },
    google_pay: { eligible: false, reason: null },
  });
  // POS Final UX Feinschliff (04.09.2026) — Inaktivitäts-Warnung.
  // Statt eines harten Abbruchs nach 30 s zeigen wir nach 40 s zunächst
  // ein globales Warn-Popup mit 20-Sekunden-Countdown. Erst wenn dieser
  // Countdown durchläuft, kommt der sichere Reset. Rechnet auf: 40 s
  // Idle + 20 s Countdown = 60 s Gesamt bis Auto-Abbruch.
  const [inactivityWarning, setInactivityWarning] = useState(false);
  const [warnRemaining, setWarnRemaining] = useState(20);
  const scanRef = useRef(null);
  const orderRef = useRef(null);
  useEffect(() => { orderRef.current = order; }, [order]);

  useEffect(() => {
    Promise.all([
      api.get("/pos/terminals?active=true"),
      api.get("/locations", { params: { type: "shop", active: true } }),
    ]).then(([tr, lr]) => {
      setTerminals(tr.data);
      setLocations(lr.data);
      if (tr.data.length > 0) {
        // Prefer the demo terminal KASSE-01 when present; otherwise take the
        // first entry (backend already orders alphabetically).
        const preferred = tr.data.find((t) => t.name === "KASSE-01") || tr.data[0];
        setTerminalId(preferred.id);
      }
    }).catch(() => { setTerminals([]); setLocations([]); });
    api.get("/pos/providers/payments")
      .then((r) => setProviders(r.data.providers))
      .catch(() => setProviders(["mock"]));
    // POS Final UX (04.09.2026) — Wallet-Eligibility ehrlich vom Server.
    // Der Sandbox-Merchant ist im Preview NICHT freigeschaltet →
    // Apple/Google-Pay-Kacheln bleiben disabled mit klarer Begründung.
    api.get("/pos/providers/wallets/eligibility")
      .then((r) => setWalletEligibility(r.data))
      .catch(() => { /* honest default: not eligible */ });
  }, []);

  const locById = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);
  const activeTerminal = useMemo(() => terminals.find((t) => t.id === terminalId), [terminals, terminalId]);
  const activeLocation = activeTerminal ? locById[activeTerminal.location_id] : null;

  // POS Realbetrieb Hardening – Paket B (03.09.2026):
  // Persistiere die ID des aktuellen PENDING-Warenkorbs pro Terminal in
  // localStorage. Damit übersteht ein Reload / Tab-Crash / Browser-Neustart
  // eine offene Zahlung, statt eine neue leere Bestellung anzulegen und
  // die alte (potenziell mit PayPal PENDING) verwaisen zu lassen. Der
  // Key ist terminal-lokal — mehrere Terminals stören sich nicht.
  const posOrderStorageKey = (tid) => `pos:pending_order:${tid}`;
  // Paket C — Merker, dass die Order per QR-Handoff bezahlt wird,
  // damit ein Reload dieselbe UI (QR statt Redirect-Pane) wiederherstellt.
  const posPaypalModeKey = (tid) => `pos:paypal_mode:${tid}`;

  async function openOrder() {
    if (!terminalId) return;

    // 1) Resume-Pfad: liegt eine persistierte Order-ID für dieses Terminal
    // vor, laden wir sie zuerst. Nur wenn sie noch existiert und tatsächlich
    // PENDING ist, übernehmen wir sie. Falls sie zusätzlich einen
    // PayPal-Intent trägt, holen wir uns per Recovery-Endpoint den
    // aktuellen serverseitigen Zahlstatus (server + PayPal = Wahrheit).
    const persistedId = window.localStorage.getItem(posOrderStorageKey(terminalId));
    if (persistedId) {
      try {
        const r = await api.get(`/pos/orders/${persistedId}`);
        const o = r.data;
        if (o.status === "PENDING" && o.terminal_id === terminalId) {
          setOrder(o);
          setReceipt(null);
          const modeQR = window.localStorage.getItem(posPaypalModeKey(terminalId)) === "qr";

          // Paket C — Kürzester, robustester QR-Recovery-Pfad.
          // Wenn das Terminal einen QR-Mode-Merker trägt, holen wir uns
          // ausschließlich den serverseitigen Payment-Status. Wir setzen
          // `paypalPending` schon dann, wenn eine `approve_url` verfügbar
          // ist — DTO-Feldabhängigkeiten (payment_provider / payment_status)
          // spielen hier keine Rolle mehr, weil die approve_url selbst
          // beweist, dass ein Intent existiert.
          if (modeQR) {
            try {
              const ps = await api.get(`/pos/orders/${persistedId}/payment-status`);
              const psd = ps.data || {};
              if (psd.status === "PAID") {
                const rec = await api.get(`/pos/orders/${persistedId}/receipt`);
                setReceipt({
                  ...rec.data,
                  points_awarded: psd.points_awarded && psd.customer_first_name ? psd.points_awarded : null,
                  customer_first_name: psd.points_awarded && psd.customer_first_name ? psd.customer_first_name : null,
                });
                setOrder(null);
                window.localStorage.removeItem(posOrderStorageKey(terminalId));
                window.localStorage.removeItem(posPaypalModeKey(terminalId));
                return;
              }
              if (psd.status === "CANCELLED") {
                window.localStorage.removeItem(posOrderStorageKey(terminalId));
                window.localStorage.removeItem(posPaypalModeKey(terminalId));
                // fresh order will be created below
              } else if (psd.next_action?.approve_url) {
                setPaypalPending({
                  order_id: persistedId,
                  approve_url: psd.next_action.approve_url,
                });
                setPaypalQRMode(true);
                toast.info("Offene PayPal-QR-Zahlung wiederhergestellt.");
                setTimeout(() => scanRef.current?.focus(), 50);
                return;
              } else {
                // Kein Intent mehr → mode-Merker aufräumen, Cart offen lassen.
                window.localStorage.removeItem(posPaypalModeKey(terminalId));
                setTimeout(() => scanRef.current?.focus(), 50);
                return;
              }
            } catch (_e) {
              // Recovery-Fehler nicht tödlich — Cart trotzdem zeigen.
              setTimeout(() => scanRef.current?.focus(), 50);
              return;
            }
          }

          // Paket B — Klassischer Redirect-Flow-Recovery (nur wenn kein
          // QR-Modus). Idempotent, greift nur bei Order mit paypal-DTO-Feldern.
          const looksLikePaypal =
            o.payment_provider === "paypal"
              && o.payment_status === "PENDING"
              && o.external_payment_id;
          if (looksLikePaypal) {
            try {
              const ps = await api.get(`/pos/orders/${persistedId}/payment-status`);
              const psd = ps.data;
              if (psd.status === "PAID") {
                // PayPal hatte während der Downzeit COMPLETED gemeldet —
                // Recovery hat serverseitig capture + confirm_payment
                // (idempotent) ausgeführt. Belegscreen anzeigen.
                const rec = await api.get(`/pos/orders/${persistedId}/receipt`);
                setReceipt({
                  ...rec.data,
                  points_awarded:
                    (psd.points_awarded && psd.points_awarded > 0
                      && psd.customer_first_name) ? psd.points_awarded : null,
                  customer_first_name:
                    (psd.points_awarded && psd.points_awarded > 0
                      && psd.customer_first_name) ? psd.customer_first_name : null,
                });
                setOrder(null);
                window.localStorage.removeItem(posOrderStorageKey(terminalId));
                return;
              }
              if (psd.status === "CANCELLED") {
                window.localStorage.removeItem(posOrderStorageKey(terminalId));
                // Fall through to fresh order creation below.
              } else if (psd.payment_status === "PENDING"
                         && psd.next_action?.approve_url) {
                setPaypalPending({
                  order_id: persistedId,
                  approve_url: psd.next_action.approve_url,
                });
                // Paket C — Wenn zuvor QR-Modus aktiv war, dieselbe
                // approve_url erneut als QR anzeigen. Kein neuer Intent.
                const wasQR = modeQR;
                if (wasQR) setPaypalQRMode(true);
                toast.info(wasQR
                  ? "Offene PayPal-QR-Zahlung wiederhergestellt."
                  : "Laufende PayPal-Zahlung fortsetzen oder abbrechen.");
                setTimeout(() => scanRef.current?.focus(), 50);
                return;
              } else if (!psd.external_payment_id && !psd.payment_provider) {
                // Kein Intent auf dem Server (mode-Key falsch positiv) —
                // Cart offen lassen, kein QR anzeigen, Merker entfernen.
                window.localStorage.removeItem(posPaypalModeKey(terminalId));
                setTimeout(() => scanRef.current?.focus(), 50);
                return;
              } else {
                // FAILED / VOIDED — nichts blockieren, Cart bleibt offen.
                setTimeout(() => scanRef.current?.focus(), 50);
                return;
              }
            } catch (_e) {
              // Recovery-Fehler ist nicht tödlich — Cart trotzdem zeigen.
              setTimeout(() => scanRef.current?.focus(), 50);
              return;
            }
          } else {
            setTimeout(() => scanRef.current?.focus(), 50);
            return;
          }
        } else {
          // Persistierte ID zeigt auf abgeschlossene / fremde Order → verwerfen.
          window.localStorage.removeItem(posOrderStorageKey(terminalId));
        }
      } catch (_e) {
        window.localStorage.removeItem(posOrderStorageKey(terminalId));
      }
    }

    // 2) Kein Resume möglich → frische Order anlegen (bisheriges Verhalten).
    try {
      const r = await api.post("/pos/orders", {
        terminal_id: terminalId,
        customer_id: customer?.id ?? null,
      });
      setOrder(r.data);
      window.localStorage.setItem(posOrderStorageKey(terminalId), r.data.id);
      setReceipt(null);
      setTimeout(() => scanRef.current?.focus(), 50);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail?.message ?? detail ?? "Bestellung konnte nicht geöffnet werden";
      toast.error(msg);
    }
  }

  useEffect(() => {
    if (terminalId && !order && !receipt) {
      openOrder().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalId]);

  async function addByEan(raw) {
    const ean = (raw || "").trim();
    const activeOrder = orderRef.current;
    if (!ean || !activeOrder) return { ok: false, message: "Kein aktiver Warenkorb" };
    try {
      const r = await api.post(`/pos/orders/${activeOrder.id}/lines`, {
        ean, quantity: 1,
      });
      setOrder(r.data);
      // Find the line we just booked (by ean via the product cache) so the
      // scanner can show the product name in its success overlay.
      const line = (r.data.lines || []).find((ln) => ln.product_name);
      const added = (r.data.lines || []).slice(-1)[0] || line;
      return { ok: true, name: added?.product_name || "Position hinzugefügt", ean };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail?.message ?? detail ?? `Produkt „${ean}" nicht gefunden`;
      toast.error(msg);
      return { ok: false, message: msg };
    }
  }

  // Produkt-Suche: fügt eine Position per ``product_id`` hinzu (kein EAN
  // erforderlich). Verwendet denselben Endpoint wie der Scanner, damit
  // Cart-Locking + Recompute-Totals identisch greifen. Toast bleibt hier
  // erhalten, damit der Dialog geschlossen bleibt und der Nutzer die
  // Rückmeldung sieht.
  async function addByProductId(productId, name) {
    const activeOrder = orderRef.current;
    if (!productId || !activeOrder) return;
    try {
      const r = await api.post(`/pos/orders/${activeOrder.id}/lines`, {
        product_id: productId, quantity: 1,
      });
      setOrder(r.data);
      toast.success(`${name || "Position"} hinzugefügt`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail?.message ?? detail ?? "Produkt konnte nicht hinzugefügt werden";
      toast.error(msg);
    }
  }

  async function handleScan(e) {
    e?.preventDefault?.();
    const raw = scanValue.trim();
    if (!raw) return;
    setScanValue("");
    const result = await addByEan(raw);
    if (result.ok) toast.success(`${result.name} hinzugefügt`);
    scanRef.current?.focus();
  }

  async function changeQty(lineId, delta) {
    const line = order.lines.find((l) => l.id === lineId);
    if (!line) return;
    const newQty = Math.max(0, line.quantity + delta);
    try {
      const r = await api.patch(
        `/pos/orders/${order.id}/lines/${lineId}`, { quantity: newQty },
      );
      setOrder(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message ?? "Menge konnte nicht geändert werden");
    }
  }

  async function removeLine(lineId) {
    try {
      const r = await api.delete(`/pos/orders/${order.id}/lines/${lineId}`);
      const remaining = (r.data?.lines || []).length;
      if (remaining === 0) {
        // POS Final Fixes (04.09.2026) — letztes Produkt entfernt =
        // sauberer Cart-Reset über dieselbe Cancel-Logik wie der
        // untenstehende „Warenkorb abbrechen"-Button. Verhindert
        // Zombie-Orders + räumt evtl. verwaiste PayPal-QR-Marker weg
        // (z. B. wenn der Kunde zuerst PayPal-QR öffnete, abbrach und
        // dann die Position löscht). Silent-Modus (kein Confirm, kein
        // extra Toast) — der leere Warenkorb ist selbst-erklärend.
        setOrder(r.data); // Sofortiges UI-Feedback
        await performCartReset({ confirm: false, silent: true });
        return;
      }
      setOrder(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message ?? "Position konnte nicht entfernt werden");
    }
  }

  async function attachCustomer() {
    const card = cardScan.trim();
    if (!card) return;
    await lookupAndAttachByCardId(card);
  }

  // Owner Review 2026-08-31 — dedizierter Kunden-Lookup-Pfad, damit
  // sowohl das manuelle Karten-Eingabefeld als auch der Kamera-Scan
  // exakt denselben Server-Round-Trip nutzen. Rückgabe folgt dem
  // BarcodeScanner-Contract ({ok, name, ean}), damit der Scanner den
  // grünen/gelben Overlay-Screen korrekt rendert und sich nach Erfolg
  // sauber schließt. Kein zweites Zuordnen bei Wiederholungs-Scan
  // (der Scanner debounced same-code 1500 ms).
  async function lookupAndAttachByCardId(rawCard) {
    const card = (rawCard || "").trim();
    if (!card) return { ok: false, message: "Leere Club-Card-ID" };
    try {
      const r = await api.get(
        `/customers/lookup?club_card_id=${encodeURIComponent(card)}`
      );
      const c = r.data;
      if (order) {
        try {
          const patched = await api.patch(`/pos/orders/${order.id}`, { customer_id: c.id });
          setOrder(patched.data);
        } catch (err) {
          const msg = err?.response?.data?.detail?.message ?? "Kunde konnte nicht zugewiesen werden";
          toast.error(msg);
          return { ok: false, message: msg };
        }
      }
      setCustomer(c);
      setShowCardModal(false);
      setCardScan("");
      toast.success(`${c.first_name} ${c.last_name} identifiziert`);
      return { ok: true, name: `${c.first_name} ${c.last_name}`, ean: c.club_card_id };
    } catch (err) {
      // Owner Review 2026-08-31: Club Card ist immer aktiv — unbekannte
      // Karte ist die einzig mögliche Fehlbedingung. Backend liefert
      // 404 „Club-Karte nicht gefunden".
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string" && detail
        ? detail
        : "Club-Karte nicht gefunden";
      toast.error(message);
      return { ok: false, message };
    }
  }

  async function detachCustomer() {
    if (order) {
      try {
        const patched = await api.patch(`/pos/orders/${order.id}`, { customer_id: null });
        setOrder(patched.data);
      } catch {
        toast.error("Kunde konnte nicht entfernt werden");
        return;
      }
    }
    setCustomer(null);
    setShowCardModal(false);
  }

  // POS Final Fixes (04.09.2026) — Cart-Reset über zentrale Cancel-
  // Logik. Wird sowohl vom „Warenkorb abbrechen"-Button (mit Confirm)
  // als auch vom Löschen der letzten Position (silent) aufgerufen.
  // Semantik ist identisch: Order-Cancel serverseitig, PayPal-/QR-
  // /Storage-Marker aufräumen, POS zurück in Ausgangszustand. PAID
  // wird nie gecancelt (Server verweigert bereits).
  async function performCartReset({ confirm = true, silent = false } = {}) {
    if (!order) return;
    // Owner-Fix (05.09.2026): native window.confirm() ersetzt durch
    // In-App-Modal. `confirm:true` öffnet den Dialog und delegiert an
    // performCartReset({confirm:false, silent:false}). `confirm:false`
    // ist der stille bzw. bereits-bestätigte Pfad.
    if (confirm) {
      setCartCancelConfirmOpen(true);
      return;
    }
    try {
      await api.post(`/pos/orders/${order.id}/cancel`);
      setOrder(null);
      setCustomer(null);
      setPaypalPending(null);
      setPaypalQRMode(false);
      setShowPaymentModal(false);
      if (terminalId) {
        window.localStorage.removeItem(posOrderStorageKey(terminalId));
        window.localStorage.removeItem(posPaypalModeKey(terminalId));
      }
      if (!silent) toast.success("Warenkorb abgebrochen");
      openOrder();
    } catch (err) {
      // Wenn der Server den Cancel verweigert (z. B. bereits PAID),
      // NICHT die UI härter aufräumen — die zahlungsseitige Wahrheit
      // gewinnt. Für den User ist es das gewohnte Fehler-Feedback.
      if (!silent) toast.error("Konnte nicht abgebrochen werden");
    }
  }

  async function cancelOrder() {
    return performCartReset({ confirm: true, silent: false });
  }

  async function checkout(provider) {
    if (!order || order.lines.length === 0) return;
    // Whitelist: der POS akzeptiert im Kundenbetrieb ausschließlich real
    // freigeschaltete Provider. Mock ist bewusst NICHT enthalten — jeder
    // Aufruf mit einem nicht gelisteten Provider bricht ohne Serverkontakt
    // ab. Fallback-Wert entfernt: früher signierte ``checkout()`` still
    // ein Mock-Sale, wenn der Aufrufer den Parameter vergaß.
    const REAL_PROVIDERS = new Set(["paypal"]);
    if (!REAL_PROVIDERS.has(provider)) {
      toast.error("Zahlungsart nicht verfügbar.");
      return;
    }
    setShowPaymentModal(false);
    setProcessing(true);
    try {
      const r = await api.post(`/pos/orders/${order.id}/checkout`, {
        payment_provider: provider,
      });
      if (r.data.status === "PAID") {
        // Loop-Punkte-Feedback: NUR beim erstmaligen PENDING→PAID-Übergang
        // ist ``points_awarded`` gesetzt. Wir zeigen es inline auf dem
        // Erfolgs-Screen an — kein separates Toast (siehe Success-Screen).
        const pts = r.data.points_awarded;
        const fn = r.data.customer_first_name;
        // Fetch the receipt DTO from the backend so print/PDF paths share
        // the same source of truth (includes company info + tax summary).
        const rec = await api.get(`/pos/orders/${order.id}/receipt`);
        setReceipt({
          ...rec.data,
          points_awarded: pts && pts > 0 && fn ? pts : null,
          customer_first_name: pts && pts > 0 && fn ? fn : null,
        });
        setOrder(null);
        setCustomer(null);
        // Paket B: persistierte Order-ID entfernen — Zahlung abgeschlossen.
        window.localStorage.removeItem(posOrderStorageKey(terminalId));
      } else if (provider === "paypal" && r.data.next_action?.approve_url) {
        // PayPal-Approval-Flow: den User in einem neuen Tab zu PayPal
        // schicken. Nach Rückkehr klickt der Kassierer „Zahlung prüfen"
        // → das ruft serverseitig /paypal-capture auf (idempotent).
        setPaypalPending({
          order_id: order.id,
          approve_url: r.data.next_action.approve_url,
        });
        window.open(r.data.next_action.approve_url, "_blank", "noopener");
      } else {
        toast.info(`Zahlung angestoßen: ${r.data.payment_status}`);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const code = d?.code;
      toast.error(
        code === "INSUFFICIENT_STOCK"
          ? "Nicht genug Bestand für alle Positionen."
          : (d?.message ?? d ?? "Zahlung fehlgeschlagen")
      );
    } finally {
      setProcessing(false);
    }
  }

  async function paypalCapture() {
    if (!paypalPending) return;
    setProcessing(true);
    try {
      const r = await api.post(
        `/pos/orders/${paypalPending.order_id}/paypal-capture`
      );
      if (r.data.status === "PAID") {
        const pts = r.data.points_awarded;
        const fn = r.data.customer_first_name;
        const rec = await api.get(`/pos/orders/${paypalPending.order_id}/receipt`);
        setReceipt({
          ...rec.data,
          points_awarded: pts && pts > 0 && fn ? pts : null,
          customer_first_name: pts && pts > 0 && fn ? fn : null,
        });
        setOrder(null);
        setCustomer(null);
        setPaypalPending(null);
        setPaypalQRMode(false);
        // Paket B: persistierte Order-ID entfernen — Zahlung abgeschlossen.
        window.localStorage.removeItem(posOrderStorageKey(terminalId));
        // Paket C: QR-Modus-Merker entfernen.
        window.localStorage.removeItem(posPaypalModeKey(terminalId));
      } else {
        toast.info(
          `PayPal-Status: ${r.data.payment_status}. Bitte im PayPal-Fenster bestätigen und erneut auf „Zahlung prüfen" klicken.`
        );
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.message ?? d ?? "PayPal-Capture fehlgeschlagen");
    } finally {
      setProcessing(false);
    }
  }

  function paypalAbort() {
    if (!paypalPending) return;
    // Owner-Fix (05.09.2026): kein window.confirm mehr — In-App-Modal.
    setPaypalAbortConfirmOpen(true);
  }

  function paypalAbortConfirmed() {
    setPaypalAbortConfirmOpen(false);
    if (!paypalPending) return;
    // Wichtige Sicherheitsregel (Paket A): der Abbruch setzt AUSSCHLIESSLICH
    // den Pending-UI-State zurück. Serverseitig ist noch nichts abgebucht
    // (Order bleibt PENDING, kein Ledger-Insert, kein Bestandsabgang). Wenn
    // der Kunde eine andere Bezahlmethode wählt, öffnen wir das
    // Payment-Modal erneut mit derselben Order-ID.
    setPaypalPending(null);
    setPaypalQRMode(false);
    // Paket C: QR-Modus-Merker entfernen — sonst würde ein späterer
    // Reload fälschlich wieder QR-Pane statt frische Auswahl zeigen.
    if (terminalId) window.localStorage.removeItem(posPaypalModeKey(terminalId));
    toast.info("Zahlung abgebrochen. Warenkorb weiterhin offen.");
  }

  // Paket C (03.09.2026) — Automatisches Polling während offener QR-Zahlung.
  // Läuft ausschließlich, wenn paypalQRMode aktiv ist und eine paypalPending-
  // Order steht. Ruft alle 2 s /payment-status. Der Server ist die einzige
  // Wahrheit — mapped APPROVED/COMPLETED → status=PAID (siehe
  // _paypal_resume_or_reconcile + _capture_and_confirm). Doppelt-Buchung
  // ist über Advisory-Lock + UNIQUE-Index unmöglich, wir polleen daher
  // sicher weiter. Bei receipt / Erfolg / Cancel / Unmount stoppt das
  // Intervall zuverlässig (return-cleanup).
  useEffect(() => {
    if (!paypalQRMode || !paypalPending) return undefined;
    let stopped = false;
    const oid = paypalPending.order_id;
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await api.get(`/pos/orders/${oid}/payment-status`);
        if (stopped) return;
        const d = r.data || {};
        if (d.status === "PAID") {
          stopped = true;
          try {
            const rec = await api.get(`/pos/orders/${oid}/receipt`);
            const pts = d.points_awarded;
            const fn = d.customer_first_name;
            setReceipt({
              ...rec.data,
              points_awarded: pts && pts > 0 && fn ? pts : null,
              customer_first_name: pts && pts > 0 && fn ? fn : null,
            });
          } catch (_e) {
            // Beleg konnte nicht geladen werden — Zahlung ist trotzdem
            // erfolgreich, wir zeigen wenigstens einen minimalen Status.
            toast.success("Zahlung erfolgreich");
          }
          setOrder(null);
          setCustomer(null);
          setPaypalPending(null);
          setPaypalQRMode(false);
          if (terminalId) {
            window.localStorage.removeItem(posOrderStorageKey(terminalId));
            window.localStorage.removeItem(posPaypalModeKey(terminalId));
          }
        } else if (d.status === "CANCELLED"
                   || (d.payment_status === "FAILED" && !d.external_payment_id)) {
          stopped = true;
          setPaypalPending(null);
          setPaypalQRMode(false);
          if (terminalId) window.localStorage.removeItem(posPaypalModeKey(terminalId));
          toast.info("PayPal-Zahlung wurde nicht abgeschlossen. Warenkorb bleibt offen.");
        }
        // sonst: PENDING → nichts tun, nächster Tick folgt.
      } catch (_e) {
        // transient — beim nächsten Tick versuchen.
      }
    };
    const iv = setInterval(tick, 2000);
    // Erster Check direkt, damit ein bereits approvedter Zustand nicht
    // erst nach 2 s finalisiert wird.
    tick();
    return () => { stopped = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalQRMode, paypalPending?.order_id, terminalId]);

  // Paket C — QR-Handoff-Checkout. Nutzt den bestehenden PayPal-
  // Checkout-Endpoint (Paket B: intent-reuse ist bereits idempotent),
  // ersetzt aber die Redirect-Pane durch einen QR-Code der approve_url.
  async function checkoutQR() {
    if (!order || processing) return;
    setShowPaymentModal(false);
    setProcessing(true);
    try {
      const r = await api.post(`/pos/orders/${order.id}/checkout`, {
        payment_provider: "paypal",
      });
      const na = r.data?.next_action || {};
      if (r.data?.status === "PAID") {
        // Extremer Sonderfall: Intent-Reuse liefert bereits PAID —
        // z. B. bei nachträglichem confirm über Recovery. Wir zeigen
        // direkt den Beleg (dieselbe Success-Logik wie klassisch).
        const pts = r.data.points_awarded;
        const fn = r.data.customer_first_name;
        const rec = await api.get(`/pos/orders/${order.id}/receipt`);
        setReceipt({
          ...rec.data,
          points_awarded: pts && pts > 0 && fn ? pts : null,
          customer_first_name: pts && pts > 0 && fn ? fn : null,
        });
        setOrder(null);
        setCustomer(null);
        if (terminalId) {
          window.localStorage.removeItem(posOrderStorageKey(terminalId));
          window.localStorage.removeItem(posPaypalModeKey(terminalId));
        }
        return;
      }
      if (!na.approve_url) {
        toast.error("PayPal-Zahlung konnte nicht gestartet werden.");
        return;
      }
      setPaypalPending({ order_id: order.id, approve_url: na.approve_url });
      setPaypalQRMode(true);
      if (terminalId) {
        window.localStorage.setItem(posPaypalModeKey(terminalId), "qr");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.message ?? d ?? "PayPal-Zahlung fehlgeschlagen");
    } finally {
      setProcessing(false);
    }
  }

  function printReceipt() {
    // Uses the print-only stylesheet in index.css. window.print blocks
    // until the dialog closes on all evergreen browsers.
    window.print();
  }

  // Erfolgs-Screen schließt Warenkorb + Kunden-Zuordnung und öffnet einen
  // frischen leeren Warenkorb — identisch für Button „Schließen" und den
  // 20-Sekunden-Auto-Close. Verändert KEINE bereits gebuchten Daten.
  // Zusätzlich Phase C: sämtliche flüchtigen POS-States (scan/cardScan/
  // Kamera) werden hart zurückgesetzt, damit der vorherige Kunde/Scan
  // NIEMALS in die nächste Transaktion mitgeschleppt wird.
  function closeReceipt() {
    setReceipt(null);
    setCustomer(null);
    setScanValue("");
    setCardScan("");
    setCameraOpen(false);
    setShowCardModal(false);
    openOrder();
  }

  // Auto-Close: nach 20 s zurück zur leeren Kasse. Timer startet erst nach
  // erfolgreichem Zahlungsabschluss (receipt gesetzt), wird sauber
  // abgebrochen, wenn der Nutzer vorher „Schließen" klickt oder die
  // Komponente unmounted. Löst KEINE neue Buchung aus.
  useEffect(() => {
    if (!receipt) return undefined;
    const t = setTimeout(() => {
      closeReceipt();
    }, 20000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt]);

  // Phase C — Verkaufsabschluss ist ein reiner Bestätigungs-State.
  // Während der 20 Sekunden dürfen KEINE Tastatur-/Scanner-Eingaben
  // Wirkung zeigen (kein neuer Scan, kein Warenkorb-Aufbau, kein neuer
  // Kunde). Wir hängen einen Capture-Listener an document, der jedes
  // Keydown abfängt. Damit sind auch Handheld-Scanner (die als Tastatur
  // emulieren) neutralisiert. Die zwei sichtbaren Buttons (Drucken /
  // Schließen) reagieren auf Klick, nicht auf Keydown.
  useEffect(() => {
    if (!receipt) return undefined;
    const block = (e) => { e.preventDefault(); e.stopPropagation(); };
    document.addEventListener("keydown", block, true);
    return () => document.removeEventListener("keydown", block, true);
  }, [receipt]);

  // -----------------------------------------------------------------------
  // POS Final UX Feinschliff (04.09.2026) — Idle-Warnung + Countdown-Reset.
  // -----------------------------------------------------------------------
  //   1) Nach 40 s Inaktivität erscheint ein globales Warn-Popup mit
  //      höchstem z-index (über PaymentModal, QR-Modal, Kategorien,
  //      Produkt-Suche, Kamera). Countdown 20 → 0 sichtbar.
  //   2) Klick auf „Kassenvorgang fortsetzen" schließt das Popup,
  //      resettet Timer + Countdown, Kassenvorgang bleibt unverändert.
  //   3) Läuft der Countdown durch, folgt der sichere Reset (server-
  //      truth check → cancel wenn PENDING → Ledger/QR/Storage
  //      aufräumen → toast). PAID gewinnt immer.
  //   4) Zwischen Popup-Erscheinen und Ablauf wird auch das PayPal-
  //      Polling-Ergebnis beobachtet: wenn Server während des Warn-
  //      Countdowns APPROVED/COMPLETED/PAID meldet, wird der Timeout
  //      abgebrochen — kein finaler Cancel.
  const inactivityTimerRef = useRef(null);
  const warningIntervalRef = useRef(null);

  const clearInactivity = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
  }, []);

  const performInactivityReset = useCallback(async () => {
    const oid = orderRef.current?.id;
    if (!oid) return;
    // P0 Sicherheitsregel (05.09.2026): Payment success wins IMMER.
    // Wir räumen den lokalen State (Order, PayPal, QR, LocalStorage)
    // NUR dann auf, wenn der Server eindeutig bestätigt, dass die
    // Order nicht mehr PAID werden kann. Bei jedem unklaren Signal
    // brechen wir den Reset ab und lassen die Session unverändert —
    // die nächste Interaktion oder der reguläre Payment-Poll klären
    // den Zustand.
    //
    // Entscheidungsbaum:
    //   1) payment-status Request #1 fehlgeschlagen → ABORT (kein Reset)
    //   2) status = PAID                              → ABORT (payment wins)
    //   3) status = CANCELLED                         → Reset erlaubt
    //   4) status = PENDING / andere:
    //        a) cancel-Request fehlgeschlagen         → ABORT
    //        b) payment-status Request #2 fehlgeschlagen → ABORT
    //        c) Re-Check = PAID                       → ABORT (payment wins)
    //        d) Re-Check ≠ PAID                       → Reset erlaubt
    let confirmedNotPaid = false;
    try {
      const ps = await api.get(`/pos/orders/${oid}/payment-status`);
      const s = (ps.data || {}).status;
      if (!s) return;                    // 1) unbekannt → nicht anfassen
      if (s === "PAID") return;          // 2) payment wins
      if (s === "CANCELLED") {
        confirmedNotPaid = true;         // 3) bereits gecancelt, sicher
      } else {
        try {
          await api.post(`/pos/orders/${oid}/cancel`);
        } catch (_e) {
          return;                        // 4a) cancel unklar → ABORT
        }
        try {
          const ps2 = await api.get(`/pos/orders/${oid}/payment-status`);
          const s2 = (ps2.data || {}).status;
          if (!s2) return;               // 4b) unbekannt → ABORT
          if (s2 === "PAID") return;     // 4c) payment wins
          confirmedNotPaid = true;       // 4d) sicher nicht PAID
        } catch (_e) {
          return;                        // 4b) Re-Check failed → ABORT
        }
      }
    } catch (_e) {
      return;                            // 1) erste Abfrage failed → ABORT
    }
    if (!confirmedNotPaid) return;

    // Ab hier: Server hat bestätigt, dass die Order nicht (mehr) PAID
    // werden kann. Lokaler destruktiver Reset ist jetzt sicher.
    if (terminalId) {
      window.localStorage.removeItem(posOrderStorageKey(terminalId));
      window.localStorage.removeItem(posPaypalModeKey(terminalId));
    }
    setOrder(null);
    setPaypalPending(null);
    setPaypalQRMode(false);
    setShowPaymentModal(false);
    setCategoryOpen(false);
    setSearchOpen(false);
    setCameraOpen(false);
    setCustomer(null);
    setCardScan("");
    toast.info("Warenkorb wegen Inaktivität zurückgesetzt.");
    setTimeout(() => openOrder().catch(() => {}), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalId]);

  // Countdown-Ticker sobald das Warn-Popup aktiv ist.
  useEffect(() => {
    if (!inactivityWarning) {
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
      return undefined;
    }
    setWarnRemaining(20);
    warningIntervalRef.current = setInterval(async () => {
      // Race: PayPal-QR-Zahlung kann während des Warn-Countdowns
      // durchgehen. Wenn Server sagt PAID → Timeout abbrechen.
      const oid = orderRef.current?.id;
      if (oid && paypalPending) {
        try {
          const ps = await api.get(`/pos/orders/${oid}/payment-status`);
          if ((ps.data || {}).status === "PAID") {
            setInactivityWarning(false);
            return;
          }
        } catch (_e) {}
      }
      setWarnRemaining((r) => {
        if (r <= 1) {
          if (warningIntervalRef.current) {
            clearInterval(warningIntervalRef.current);
            warningIntervalRef.current = null;
          }
          setInactivityWarning(false);
          performInactivityReset();
          return 20;
        }
        return r - 1;
      });
    }, 1000);
    return () => {
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
    };
  }, [inactivityWarning, paypalPending, performInactivityReset]);

  const kickInactivity = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    // Wenn das Warn-Popup gerade offen ist, wird der 40-s-Timer NICHT
    // neu gesetzt — nur der Klick auf „Kassenvorgang fortsetzen"
    // resettet, siehe `resumeSession` unten.
    if (inactivityWarning) return;
    const active = orderRef.current;
    if (!active?.lines?.length) return; // leerer Warenkorb → kein Timer
    if (receipt) return;                // Erfolgsscreen → kein Timer
    inactivityTimerRef.current = setTimeout(() => {
      setWarnRemaining(20);
      setInactivityWarning(true);
    }, 40000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt, inactivityWarning]);

  const resumeSession = useCallback(() => {
    setInactivityWarning(false);
    setWarnRemaining(20);
    // Timer wird durch den useEffect-Rerun auf `inactivityWarning`
    // automatisch neu gestartet, sobald kickInactivity läuft.
  }, []);

  useEffect(() => {
    kickInactivity();
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [order?.id, order?.lines?.length, showPaymentModal, paypalQRMode,
      categoryOpen, searchOpen, showCardModal, kickInactivity]);

  // Beim Unmount / Logout / Order-Abschluss (receipt) alle Timer weg.
  useEffect(() => () => clearInactivity(), [clearInactivity]);
  useEffect(() => {
    if (receipt) {
      clearInactivity();
      setInactivityWarning(false);
    }
  }, [receipt, clearInactivity]);

  // -----------------------------------------------------------------------
  // POS Final UX (04.09.2026) — Scanner-Fokus-Watchdog.
  // -----------------------------------------------------------------------
  // Der EAN-Input soll bei jedem POS-Leerlauf sofort scannerbereit sein
  // (Hardware-/USB-/Bluetooth-Scanner sendet Keystrokes ohne Klick).
  // Regeln:
  //   • autoFocus + explizite Fokus-Rückgabe nach Modal-Close (bereits
  //     an den relevanten Stellen implementiert).
  //   • Watchdog: falls kein Modal offen ist und kein Text-Input aktiv,
  //     stellen wir den Fokus zurück auf das Scan-Feld. Der Watchdog
  //     schaltet sich AUS, wenn der Benutzer bewusst im Produkt-Such-
  //     Input oder Karten-Input schreibt (activeElement in Suche/Karte).
  //   • Während Zahlungs-/QR-/Kamera-/Kartenmodal wird nichts erzwungen —
  //     dort haben die Modale Ihren eigenen Fokus-Kontext.
  const isModalOpen =
    showPaymentModal || paypalQRMode || cameraOpen || cardCameraOpen ||
    showCardModal || searchOpen || categoryOpen || !!receipt;
  useEffect(() => {
    if (isModalOpen) return undefined;
    // Direkt nach jedem State-Wechsel Fokus zurückholen.
    const tid = setTimeout(() => {
      const ae = document.activeElement;
      const tag = ae?.tagName;
      const testId = ae?.getAttribute?.("data-testid") || "";
      // Nicht klauen, wenn im Produktsuch-Input oder Karten-Input.
      const isUserTyping =
        testId === "pos-product-search-input" ||
        testId === "pos-card-input" ||
        (tag === "TEXTAREA");
      if (isUserTyping) return;
      if (ae !== scanRef.current) scanRef.current?.focus?.();
    }, 60);
    return () => clearTimeout(tid);
  }, [isModalOpen, order?.id, order?.lines?.length]);

  // Zusätzlich: Wenn irgendwo im POS-Body geklickt wird und danach kein
  // interaktives Ziel-Element den Fokus behält, geben wir ihn dem
  // Scanner zurück. Damit funktioniert ein Hardware-Scanner-Trigger
  // auch, wenn der Kassierer irgendwo ins Leere getippt hat.
  useEffect(() => {
    if (isModalOpen) return undefined;
    const onWindowClick = () => {
      setTimeout(() => {
        const ae = document.activeElement;
        if (!ae || ae === document.body) scanRef.current?.focus?.();
      }, 30);
    };
    window.addEventListener("click", onWindowClick);
    return () => window.removeEventListener("click", onWindowClick);
  }, [isModalOpen]);

  const total = order?.total_cents ?? 0;

  // POS Phase C.2/C.3 — Werbebanner unter der Kassenoberfläche.
  // Bis zu 3 Slots, automatische Rotation bei ≥ 2 aktiven Bannern.
  //   - 0 aktive Banner → gar NICHTS rendern (kein Rahmen, kein Platzhalter).
  //   - 1 aktiver Banner → statisches Bild, keine Animation.
  //   - 2/3 aktive Banner → sanfter Crossfade im Wechsel alle 9 s.
  // Feste Fläche (h-24 / sm:h-32 / lg:h-40) + object-cover → keine
  // Layout-Verschiebung, kein Verzerren. Layer sind `pointer-events-none`
  // + `select-none` → keine Klicks/Drag/Keyboard-Wirkung, auch nicht
  // während des 20 s-Abschluss-States.
  const bannerSlots = [
    { url: settings?.pos_ad_banner_data_url,   active: settings?.pos_ad_banner_active },
    { url: settings?.pos_ad_banner_2_data_url, active: settings?.pos_ad_banner_2_active },
    { url: settings?.pos_ad_banner_3_data_url, active: settings?.pos_ad_banner_3_active },
  ];
  const activeBanners = bannerSlots
    .map((b, i) => ({ ...b, slot: i + 1 }))
    .filter((b) => (b.active + "").trim() === "1" && !!b.url);
  const [bannerIdx, setBannerIdx] = useState(0);
  useEffect(() => {
    if (activeBanners.length <= 1) { setBannerIdx(0); return undefined; }
    const t = setInterval(() => {
      setBannerIdx((i) => (i + 1) % activeBanners.length);
    }, 9000);
    return () => clearInterval(t);
  }, [activeBanners.length]);
  const posAdBanner = activeBanners.length === 0 ? null : (
    <section
      data-testid="pos-ad-banner"
      data-banner-slots={activeBanners.length}
      className="lg:col-span-2 mt-2 relative rounded-sm overflow-hidden border border-zinc-800 bg-zinc-950 pointer-events-none select-none h-24 sm:h-32 lg:h-40"
      aria-hidden="true"
    >
      {activeBanners.map((b, i) => (
        <img
          key={b.slot}
          src={b.url}
          alt=""
          draggable={false}
          data-banner-slot={b.slot}
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
            activeBanners.length === 1 || i === bannerIdx ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </section>
  );

  if (receipt) {
    return (
      <>
        <div className="min-h-[80vh] flex items-center justify-center no-print">
          <div className="w-full max-w-lg border border-zinc-800 rounded-lg bg-zinc-950 p-8 text-center" data-testid="pos-receipt">
            <div className="text-cyan-400 text-6xl font-black mb-4">✓</div>
            <div className="text-2xl font-bold mb-1">Zahlung erfolgreich</div>
            <div className="text-zinc-400 mb-6 font-mono text-sm">{receipt.receipt_number}</div>
            <div className="text-4xl font-black text-cyan-400 my-6">{formatCents(receipt.total_cents)}</div>
            {receipt.points_awarded && receipt.customer_first_name && pointsOn ? (
              <div
                data-testid="pos-receipt-points"
                className="mt-1 mb-6 flex flex-col items-center gap-1"
              >
                <div className="text-2xl sm:text-3xl font-black text-cyan-400 uppercase tracking-widest">
                  +{receipt.points_awarded} {brandName} Punkte
                </div>
                <div className="text-xs text-zinc-400 uppercase tracking-wider">
                  für {receipt.customer_first_name}
                </div>
              </div>
            ) : null}
            <div className="text-left border-t border-zinc-800 pt-4 space-y-2">
              {receipt.lines.map((ln, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span>{ln.quantity}× {ln.name}</span>
                  <span className="font-mono">{formatCents(ln.line_gross_cents ?? ln.line_net_cents)}</span>
                </div>
              ))}
            </div>
            {receipt.customer && (
              <div className="mt-4 text-xs text-zinc-400 font-mono">
                Kunde: {receipt.customer.name} · {receipt.customer.club_card_id}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-8">
              <Button data-testid="pos-print-btn" variant="outline" className="h-14 border-zinc-700" onClick={printReceipt}>
                <Printer size={18} className="mr-2" /> Drucken
              </Button>
              <Button data-testid="pos-close-receipt-btn" className="h-14" onClick={closeReceipt}>
                Schließen
              </Button>
            </div>
          </div>
        </div>
        {posAdBanner && (
          <div className="mx-auto w-full max-w-lg no-print">
            {posAdBanner}
          </div>
        )}
        <PrintableReceipt receipt={receipt} />
      </>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 min-h-[80vh]" data-testid="pos-root">
      {/* Left: scan area */}
      <div>
        <div className="mb-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <ShoppingCart className="text-cyan-400 shrink-0" size={28} />
            <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight truncate">Kasse</h1>
            {/* Phase C — Lock-Button ausschließlich für Desktop/Kassen-PC.
                POS Final Fixes (04.09.2026): Sperren/Entsperren nutzen
                denselben Layout-Slot mit fester Breite, damit die
                „KASSE"-Zeile im gesperrten wie im entsperrten Zustand
                exakt gleich aussieht. Kein `absolute`/floating —
                Button liegt fest neben der Überschrift. */}
            {!lockActive && (
              <button
                type="button"
                data-testid="pos-lock-btn"
                onClick={() => setLockModalOpen(true)}
                className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-zinc-700 hover:border-cyan-500 hover:text-cyan-400 text-zinc-400 text-xs uppercase tracking-widest"
                aria-label="POS Lock Mode aktivieren"
              >
                <Lock size={14} /> Sperren
              </button>
            )}
            {lockActive && (
              <button
                type="button"
                data-testid="pos-lock-indicator"
                onClick={() => setUnlockModalOpen(true)}
                className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-cyan-500/60 bg-cyan-500/10 text-cyan-400 hover:text-cyan-300 hover:border-cyan-400 text-xs uppercase tracking-widest"
                aria-label="POS Lock Mode verlassen — Admin-PIN erforderlich"
                title="Entsperren per Admin-Passwort"
              >
                <Unlock size={14} /> Entsperren
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <select
              data-testid="pos-terminal-select"
              className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm flex-1 sm:flex-none min-w-0"
              value={terminalId} onChange={(e) => setTerminalId(e.target.value)}
            >
              {terminals.map((t) => {
                const loc = locById[t.location_id];
                return <option key={t.id} value={t.id}>{t.name}{loc ? ` — ${loc.name}` : ""}</option>;
              })}
            </select>
            {activeLocation && (
              <div className="text-xs text-cyan-400 font-mono uppercase w-full sm:w-auto"
                data-testid="pos-active-location">
                Bestand: {activeLocation.name}
              </div>
            )}
            <Button
              data-testid="pos-club-btn"
              variant={customer ? "outline" : "default"}
              onClick={() => setShowCardModal(true)}
              className={
                customer
                  ? "border-emerald-400 text-emerald-300 max-w-[60vw] truncate"
                  : "bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider max-w-[60vw]"
              }
            >
              <CreditCard size={16} className="mr-2 shrink-0" />
              <span className="truncate">
                {customer ? `${customer.first_name} ${customer.last_name}` : "Club Karte anmelden"}
              </span>
            </Button>
          </div>
        </div>

        <form onSubmit={handleScan} className="mb-6 space-y-2">
          {/* POS Final UX (04.09.2026) — Zeile 1: EAN-Feld allein über
              volle Breite (Hardware-/Bluetooth-Scanner tippen hier direkt).
              Zeile 2: drei gleich große Touch-Buttons.  */}
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-400" size={22} />
            <Input
              data-testid="pos-scan-input"
              ref={scanRef}
              value={scanValue}
              autoFocus
              placeholder="EAN scannen oder eingeben..."
              onChange={(e) => setScanValue(e.target.value)}
              className="pl-12 h-16 sm:h-20 !text-xl sm:!text-2xl font-mono tracking-wider bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button
              type="button"
              data-testid="pos-camera-btn"
              onClick={() => setCameraOpen(true)}
              disabled={!order}
              aria-label="Kamera aktivieren"
              title="Kamera aktivieren"
              className="h-16 sm:h-20 px-2 rounded-sm bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-cyan-400 flex flex-col items-center justify-center gap-1"
            >
              <Camera size={22} />
              {/* Owner-Fix (05.09.2026): Label blendet sich weg, wenn die
                  Sidebar offen ist und der Platz eng wird. Regel:
                  - <lg (Sidebar aus): Text sichtbar
                  - lg (Sidebar an, Content ~760 px): Text weg → nur Icon
                  - xl+ (Sidebar an, Content ~1016 px+): Text wieder sichtbar
                  Kein Text-Wrap, kein Overflow. */}
              <span className="pos-action-label uppercase tracking-wider text-[11px] sm:text-xs whitespace-nowrap">
                Kamera aktivieren
              </span>
            </Button>
            <Button
              type="button"
              data-testid="pos-product-search-btn"
              onClick={() => setSearchOpen(true)}
              disabled={!order}
              aria-label="Produkt suchen"
              title="Produkt suchen"
              className="h-16 sm:h-20 px-2 rounded-sm bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-cyan-400 flex flex-col items-center justify-center gap-1"
            >
              <Search size={22} />
              <span className="pos-action-label uppercase tracking-wider text-[11px] sm:text-xs whitespace-nowrap">
                Produkt suchen
              </span>
            </Button>
            <Button
              type="button"
              data-testid="pos-category-btn"
              onClick={() => setCategoryOpen(true)}
              disabled={!order}
              aria-label="Kategorien"
              title="Kategorien"
              className="h-16 sm:h-20 px-2 rounded-sm bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-cyan-400 flex flex-col items-center justify-center gap-1"
            >
              <LayoutGrid size={22} />
              <span className="pos-action-label uppercase tracking-wider text-[11px] sm:text-xs whitespace-nowrap">
                Kategorien
              </span>
            </Button>
          </div>
        </form>

        <BarcodeScanner
          open={cameraOpen}
          onClose={() => { setCameraOpen(false); setTimeout(() => scanRef.current?.focus(), 50); }}
          onDetected={async (code) => await addByEan(code)}
          facingMode="user"
          neutralUi
          neutralTitle="Produkt scannen"
          neutralHint="Produkt in 15–20 cm Abstand vor die Kamera halten."
        />

        <div className="space-y-2" data-testid="pos-lines">
          {order?.lines?.length ? order.lines.map((ln) => (
            <div key={ln.id}
              className="flex flex-wrap items-center gap-y-2 gap-x-2 bg-zinc-900 border border-zinc-800 rounded-sm px-3 sm:px-4 py-3">
              <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                <div className="font-medium truncate">{ln.product_name}</div>
                <div className="text-xs text-zinc-500 font-mono truncate">{formatCents(ln.unit_price_cents)} / Stk.</div>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Button data-testid={`pos-line-minus-${ln.id}`} size="icon" variant="outline"
                  className="h-10 w-10 sm:h-11 sm:w-11 border-zinc-700 shrink-0" onClick={() => changeQty(ln.id, -1)}>
                  <Minus size={18} />
                </Button>
                <div className="w-10 sm:w-12 text-center font-black text-lg sm:text-xl shrink-0" data-testid={`pos-line-qty-${ln.id}`}>{ln.quantity}</div>
                <Button data-testid={`pos-line-plus-${ln.id}`} size="icon" variant="outline"
                  className="h-10 w-10 sm:h-11 sm:w-11 border-zinc-700 shrink-0" onClick={() => changeQty(ln.id, 1)}>
                  <Plus size={18} />
                </Button>
                <div className="w-20 sm:w-24 text-right font-mono font-bold shrink-0" data-testid={`pos-line-total-${ln.id}`}>
                  {formatCents(ln.line_total_cents)}
                </div>
                <Button data-testid={`pos-line-remove-${ln.id}`} size="icon" variant="ghost"
                  className="text-red-400 h-10 w-10 sm:h-11 sm:w-11 shrink-0" onClick={() => removeLine(ln.id)}>
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          )) : (
            <div className="text-center text-zinc-500 py-16 border border-dashed border-zinc-800 rounded-sm">
              Warenkorb ist leer — Barcode scannen zum Starten.
            </div>
          )}
        </div>
      </div>

      {/* Right: total + checkout */}
      <div className="lg:sticky lg:top-6 self-start border border-zinc-800 rounded-lg bg-zinc-950 p-4 sm:p-6 min-w-0" data-testid="pos-checkout-panel">
        <div className="text-sm uppercase tracking-widest text-zinc-500 mb-2">Zu zahlen</div>
        <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-cyan-400 font-mono mb-6 sm:mb-8 tabular-nums truncate" data-testid="pos-total">
          {formatCents(total)}
        </div>
        <div className="space-y-2 mb-6 text-sm text-zinc-400 border-y border-zinc-800 py-4">
          <div className="flex justify-between"><span>Zwischensumme</span><span className="font-mono">{formatCents(order?.subtotal_cents ?? 0)}</span></div>
          <div className="flex justify-between"><span>MwSt</span><span className="font-mono">{formatCents(order?.tax_cents ?? 0)}</span></div>
        </div>
        <Button
          data-testid="pos-checkout-btn"
          disabled={!order?.lines?.length || processing || !!paypalPending}
          className="w-full h-20 text-2xl font-black bg-cyan-500 hover:bg-cyan-400 text-black"
          onClick={() => setShowPaymentModal(true)}
        >
          <CreditCard size={26} className="mr-3" />
          {processing ? "Verbucht..." : "Bezahlen"}
        </Button>
        {paypalPending && !paypalQRMode && (
          <div
            data-testid="pos-paypal-pending"
            className="mt-3 border border-zinc-700 bg-zinc-900 rounded-sm p-4 text-sm space-y-3"
          >
            <div className="text-cyan-400 font-semibold uppercase tracking-wider text-xs">
              PayPal-Zahlung offen
            </div>
            <div className="text-zinc-300 leading-relaxed">
              PayPal wurde geöffnet. Schließe die Zahlung dort ab und kehre
              anschließend hierher zurück.
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                data-testid="pos-paypal-capture-btn"
                onClick={paypalCapture}
                disabled={processing}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
              >
                {processing ? "Prüfe..." : "Zahlung prüfen"}
              </Button>
              <Button
                data-testid="pos-paypal-reopen-btn"
                variant="outline"
                onClick={() => window.open(paypalPending.approve_url, "_blank", "noopener")}
                className="border-zinc-700"
              >
                PayPal erneut öffnen
              </Button>
              <Button
                data-testid="pos-paypal-abort-btn"
                variant="ghost"
                onClick={paypalAbort}
                className="text-red-400"
              >
                Zahlung abbrechen
              </Button>
            </div>
          </div>
        )}
        <Button
          data-testid="pos-cancel-btn"
          variant="ghost"
          disabled={!order}
          className="w-full mt-3 text-red-400"
          onClick={cancelOrder}
        >
          Warenkorb abbrechen
        </Button>
      </div>

      {/* POS Phase C.2 — Werbebanner (nur wenn aktiv+Bild vorhanden).
          `lg:col-span-2` spannt volle Breite unter beiden Spalten. */}
      {posAdBanner}

      {/* Club card modal */}
      {showCardModal && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" data-testid="pos-card-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg font-bold uppercase tracking-wide">{brandName} Club Karte</div>
              <Button size="icon" variant="ghost" onClick={() => setShowCardModal(false)}><X size={18} /></Button>
            </div>
            <Input
              autoFocus
              placeholder="Club-Card-ID scannen..."
              data-testid="pos-card-input"
              value={cardScan}
              onChange={(e) => setCardScan(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") attachCustomer(); }}
              className="h-14 text-lg font-mono bg-zinc-950 border-zinc-700 focus-visible:ring-cyan-400"
            />
            {/* Owner Review 2026-08-31 — Mobile Kamera-Scan-Fallback.
                Öffnet dieselbe BarcodeScanner-Infrastruktur wie der
                Produkt-Scan, aber ausschließlich mit QR_CODE-Format.
                Der manuelle Input bleibt daneben bewusst erhalten. */}
            <Button
              type="button"
              data-testid="pos-card-camera-btn"
              onClick={() => setCardCameraOpen(true)}
              className="w-full mt-3 h-12 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-cyan-400"
            >
              <QrCode size={18} className="mr-2" /> Karte scannen
            </Button>
            <div className="flex gap-2 mt-4">
              <Button data-testid="pos-card-attach-btn" onClick={attachCustomer} className="flex-1">Zuweisen</Button>
              {customer && (
                <Button data-testid="pos-card-detach-btn" variant="outline" className="border-zinc-700" onClick={detachCustomer}>
                  Entfernen
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Owner Review 2026-08-31 — Club-Card-Kamera-Scan.
          Getrennter BarcodeScanner-Kontext mit QR_CODE-Format. Nach
          erfolgreichem Scan schließt die Kamera automatisch und ordnet
          den Kunden am aktiven Warenkorb zu. Bei ungültigem QR oder
          unbekannter Card zeigt der Scanner selbst den amberfarbenen
          Fehler und bleibt offen — der Kassierer kann direkt nochmal
          scannen oder auf X drücken. */}
      <BarcodeScanner
        open={cardCameraOpen}
        onClose={() => setCardCameraOpen(false)}
        onDetected={async (code) => await lookupAndAttachByCardId(code)}
        formats={CARD_SCAN_FORMATS}
        facingMode="user"
        neutralUi
        neutralTitle="Club-Karte scannen"
        neutralHint="Club-Karten-QR in 15–20 cm Abstand vor die Kamera halten."
        successTitle="Kunde erkannt"
        footerHint="Halte den Club-Card-QR ruhig im Rahmen. Nach erfolgreichem Scan wird der Kunde automatisch zugeordnet."
      />

      {/* Produkt-Suche als Dialog (V1 Core Completion). */}
      <PosProductSearchDialog
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setTimeout(() => scanRef.current?.focus(), 50); }}
        onPick={(p) => { addByProductId(p.id, p.name); }}
      />

      {/* POS Final UX (04.09.2026) — Kategorie-Ansicht.
          Touch-Kategorien → große Produktkacheln → Auswahl bucht per
          product_id (identischer Write-Pfad wie Scanner / Suche). Kein
          neues Datenmodell — nutzt die vorhandene ``category``-Spalte
          und ``sales_price`` / ``image_url``-Felder des Produkts. */}
      <PosCategoryPicker
        open={categoryOpen}
        onClose={() => { setCategoryOpen(false); setTimeout(() => scanRef.current?.focus(), 50); }}
        onPick={(p) => { addByProductId(p.id, p.name); }}
      />


      {/* Owner-Fix (05.09.2026) — In-App-Confirm-Dialoge (ersetzen die
          nativen window.confirm() im POS). Liegen mit z-[9998] über den
          Payment- und QR-Modals (z-40/50), aber UNTER dem Inaktivitäts-
          Warnpopup (z-[9999]), damit dessen Countdown weiterhin gewinnt. */}
      {cartCancelConfirmOpen && (
        <div
          className="fixed inset-0 z-[9998] bg-black/85 flex items-center justify-center p-4"
          data-testid="pos-cancel-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="pos-cancel-confirm-title"
        >
          <div className="w-full max-w-md bg-zinc-900 border border-rose-500/60 rounded-2xl p-6 sm:p-8 shadow-2xl">
            <div
              id="pos-cancel-confirm-title"
              className="font-display text-base sm:text-lg font-black uppercase tracking-widest text-rose-400"
            >
              Warenkorb abbrechen?
            </div>
            <div className="mt-3 text-sm text-zinc-300 leading-relaxed">
              Alle Positionen im aktuellen Warenkorb werden entfernt und
              die Order storniert. Dieser Schritt kann nicht rückgängig
              gemacht werden.
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                data-testid="pos-cancel-confirm-decline-btn"
                variant="outline"
                onClick={() => setCartCancelConfirmOpen(false)}
                className="h-12 border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-xl font-bold uppercase tracking-widest text-xs"
              >
                Zurück
              </Button>
              <Button
                data-testid="pos-cancel-confirm-accept-btn"
                onClick={() => {
                  setCartCancelConfirmOpen(false);
                  performCartReset({ confirm: false, silent: false });
                }}
                className="h-12 bg-rose-500 hover:bg-rose-400 text-zinc-950 rounded-xl font-bold uppercase tracking-widest text-xs"
              >
                Ja, abbrechen
              </Button>
            </div>
          </div>
        </div>
      )}

      {paypalAbortConfirmOpen && (
        <div
          className="fixed inset-0 z-[9998] bg-black/85 flex items-center justify-center p-4"
          data-testid="pos-paypal-abort-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="pos-paypal-abort-confirm-title"
        >
          <div className="w-full max-w-md bg-zinc-900 border border-amber-500/60 rounded-2xl p-6 sm:p-8 shadow-2xl">
            <div
              id="pos-paypal-abort-confirm-title"
              className="font-display text-base sm:text-lg font-black uppercase tracking-widest text-amber-400"
            >
              PayPal-Zahlung abbrechen?
            </div>
            <div className="mt-3 text-sm text-zinc-300 leading-relaxed">
              Der Warenkorb bleibt erhalten und du kannst danach eine
              andere Bezahlmethode wählen. Es wurde noch nichts gebucht.
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                data-testid="pos-paypal-abort-confirm-decline-btn"
                variant="outline"
                onClick={() => setPaypalAbortConfirmOpen(false)}
                className="h-12 border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-xl font-bold uppercase tracking-widest text-xs"
              >
                Weiter zahlen
              </Button>
              <Button
                data-testid="pos-paypal-abort-confirm-accept-btn"
                onClick={paypalAbortConfirmed}
                className="h-12 bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded-xl font-bold uppercase tracking-widest text-xs"
              >
                Zahlung abbrechen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* POS Final UX Feinschliff (04.09.2026) — Inaktivitäts-Warnung.
          Globales Warn-Popup mit sichtbarem Countdown und explizitem
          Weiter-Button. HÖCHSTER z-index (`z-[9999]`) — liegt über
          Payment-Modal (z-40), QR-Modal (z-50), Kategorien-Modal (z-40),
          Kamera-Overlay (`z-[100]`) etc. Backdrop-Klick löst KEINEN
          Weiter aus, damit ein versehentlicher Klick den Countdown
          nicht heimlich resettet — der Kassierer muss bewusst
          bestätigen. */}
      {inactivityWarning && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          data-testid="pos-inactivity-warning"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="pos-inactivity-warning-title"
        >
          <div className="w-full max-w-md bg-zinc-900 border border-amber-500/60 rounded-2xl p-6 sm:p-8 text-center shadow-2xl">
            <div
              id="pos-inactivity-warning-title"
              className="font-display text-base sm:text-lg font-black uppercase tracking-widest text-amber-400"
            >
              Kassenvorgang endet in
            </div>
            <div
              className="mt-4 text-6xl sm:text-7xl font-black font-mono text-zinc-100 tabular-nums"
              data-testid="pos-inactivity-warning-countdown"
            >
              {warnRemaining}
            </div>
            <div className="mt-1 text-sm text-zinc-400 uppercase tracking-widest">
              Sekunden
            </div>
            <div className="mt-4 text-sm text-zinc-300 leading-relaxed">
              Es wurde eine Weile keine Aktion registriert. Ohne
              Rückmeldung wird der Warenkorb aus Sicherheitsgründen
              automatisch geleert.
            </div>
            <Button
              data-testid="pos-inactivity-warning-resume-btn"
              onClick={resumeSession}
              className="mt-6 w-full h-14 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-xl font-bold uppercase tracking-widest text-sm"
            >
              Kassenvorgang fortsetzen
            </Button>
          </div>
        </div>
      )}

      {/* Paket C (03.09.2026) — QR-Handoff-Modal.
          UX-Fix 03.09.2026: modernisierte Anmutung mit X-Close (=Abbruch),
          cyanem Glow um den QR-Code, Sekundär-Zahlungsarten (Apple Pay /
          Google Pay) als vorbereitete Pills unter dem QR + weiterhin
          Bankkarte disabled. Der eigentliche Zahlungs-/Recovery-Flow
          bleibt unangetastet — kein neuer PayPal-Intent, gleiche
          approve_url, Reload restauriert exakt diesen Modal-Zustand.  */}
      {paypalPending && paypalQRMode && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-auto"
          data-testid="pos-paypal-qr"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-paypal-qr-title"
        >
          <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-5 sm:p-7 text-center shadow-2xl relative">
            <button
              type="button"
              data-testid="pos-paypal-qr-close-btn"
              onClick={paypalAbort}
              className="absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition"
              aria-label="Zahlung abbrechen und schließen"
            >
              <X size={18} />
            </button>
            <div
              id="pos-paypal-qr-title"
              className="font-display text-base sm:text-lg font-black uppercase tracking-widest text-zinc-100 mt-1"
            >
              Mit PayPal bezahlen
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed mt-2 px-2">
              QR-Code mit dem Smartphone scannen und die Zahlung auf
              dem eigenen Handy abschließen.
            </div>
            <div className="my-6 flex justify-center">
              <div
                className="p-3 rounded-2xl bg-white shadow-[0_0_40px_-8px_rgba(34,211,238,0.55)] ring-2 ring-cyan-400/60"
                data-testid="pos-paypal-qr-code"
              >
                <QRCodeSVG
                  value={paypalPending.approve_url}
                  size={220}
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>
            <div
              className="inline-flex items-center gap-2 text-cyan-400 text-sm font-medium"
              data-testid="pos-paypal-qr-status"
            >
              <Clock size={16} className="animate-pulse" />
              <span>Warte auf Zahlung …</span>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-zinc-800" />
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Oder andere Zahlungsart wählen
              </div>
              <div className="flex-1 h-px bg-zinc-800" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="pos-paypal-qr-apple-btn"
                onClick={() => {
                  const w = walletEligibility?.apple_pay || {};
                  toast.info(w.eligible
                    ? "Apple Pay Handoff in Vorbereitung — kein Sale ausgelöst."
                    : (w.reason || "Apple Pay steht auf diesem Gerät nicht zur Verfügung."));
                }}
                disabled={!walletEligibility?.apple_pay?.eligible}
                title={walletEligibility?.apple_pay?.reason || undefined}
                className="h-12 rounded-xl bg-black border border-zinc-700 flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ApplePayLogo />
              </button>
              <button
                type="button"
                data-testid="pos-paypal-qr-google-btn"
                onClick={() => {
                  const w = walletEligibility?.google_pay || {};
                  toast.info(w.eligible
                    ? "Google Pay Handoff in Vorbereitung — kein Sale ausgelöst."
                    : (w.reason || "Google Pay steht auf diesem Gerät nicht zur Verfügung."));
                }}
                disabled={!walletEligibility?.google_pay?.eligible}
                title={walletEligibility?.google_pay?.reason || undefined}
                className="h-12 rounded-xl bg-white border border-zinc-300 flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <GooglePayLogo />
              </button>
            </div>
            <button
              type="button"
              data-testid="pos-paypal-qr-card-btn"
              disabled
              title="Bankkarte am POS ist derzeit deaktiviert."
              className="mt-2 w-full h-12 rounded-xl bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-90"
            >
              <CreditCard size={16} className="text-cyan-400" />
              <span className="font-semibold">Bankkarte</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-400 border border-zinc-600 rounded-full px-2 py-0.5">
                derzeit nicht verfügbar
              </span>
            </button>
            <button
              type="button"
              data-testid="pos-paypal-qr-abort-btn"
              onClick={paypalAbort}
              className="mt-4 w-full text-red-400 hover:text-red-300 font-semibold text-sm py-2"
            >
              Zahlung abbrechen
            </button>
            <button
              type="button"
              data-testid="pos-paypal-qr-open-here-btn"
              onClick={() => window.open(paypalPending.approve_url, "_blank", "noopener")}
              className="mt-1 w-full text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
            >
              PayPal auf diesem Gerät öffnen
            </button>
          </div>
        </div>
      )}

      {/* Zahlungsart-Auswahl (Paket A — Realbetrieb Hardening 01.09.2026).
          Wird ausschließlich über den großen „Bezahlen"-Button geöffnet.
          PayPal ist die einzige aktive Kunden-Zahlungsart. Bankkarte
          bleibt sichtbar als „coming soon", ist aber disabled und
          löst KEINEN Request aus. Ein Mock-Provider erscheint hier
          NIE — er ist ausschließlich intern für DEV-/Automation-Tests
          über die Whitelist in ``checkout()`` erreichbar. */}
      {showPaymentModal && (
        <div
          className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4"
          data-testid="pos-payment-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-7 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="font-display text-base sm:text-lg font-black uppercase tracking-widest text-zinc-100">
                Zahlungsart wählen
              </div>
              <button
                type="button"
                data-testid="pos-payment-modal-close"
                onClick={() => setShowPaymentModal(false)}
                className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition"
                aria-label="Schließen"
              >
                <X size={18} />
              </button>
            </div>
            {/* Primär-Zahlungsarten als runde Marken-Buttons.
                UX-Fix 03.09.2026: touch-freundliche Logo-Kacheln statt
                schwerer Textbuttons. PayPal ist der einzige aktive
                Provider → routet direkt in den QR-Flow (checkoutQR).
                Apple Pay & Google Pay sind visuell vorbereitet, technisch
                „bald verfügbar" (disabled) — kein Serverkontakt möglich. */}
            <div className="flex justify-center gap-4 sm:gap-6 mb-6">
              <BrandCircle
                testid="pos-pay-paypal-qr-btn"
                onClick={checkoutQR}
                disabled={!providers.includes("paypal") || processing}
                bg="bg-[#ffc439]"
                ring="ring-[#ffc439]/40"
                label="PayPal"
                logo={<PaypalLogo />}
              />
              <BrandCircle
                testid="pos-pay-apple-btn"
                onClick={() => {
                  const w = walletEligibility?.apple_pay || {};
                  if (w.eligible) {
                    // Live-Pfad wird beim Merchant-Enablement in einer
                    // separaten Aufgabe implementiert (PayPal Advanced
                    // Checkout, Apple Pay Domain Registration). Bis dahin:
                    // Toast + kein Serverkontakt.
                    toast.info("Apple Pay Handoff in Vorbereitung — kein Sale ausgelöst.");
                  } else {
                    toast.info(w.reason || "Apple Pay steht auf diesem Gerät nicht zur Verfügung.");
                  }
                }}
                disabled={!walletEligibility?.apple_pay?.eligible}
                comingSoon={!walletEligibility?.apple_pay?.eligible}
                bg="bg-black"
                ring="ring-zinc-700"
                label="Apple Pay"
                logo={<ApplePayLogo size={24} />}
              />
              <BrandCircle
                testid="pos-pay-google-btn"
                onClick={() => {
                  const w = walletEligibility?.google_pay || {};
                  if (w.eligible) {
                    toast.info("Google Pay Handoff in Vorbereitung — kein Sale ausgelöst.");
                  } else {
                    toast.info(w.reason || "Google Pay steht auf diesem Gerät nicht zur Verfügung.");
                  }
                }}
                disabled={!walletEligibility?.google_pay?.eligible}
                comingSoon={!walletEligibility?.google_pay?.eligible}
                bg="bg-white"
                ring="ring-zinc-300"
                label="Google Pay"
                logo={<GooglePayLogo size={24} />}
              />
            </div>
            <button
              type="button"
              data-testid="pos-pay-card-btn"
              disabled
              title="Bankkarte am POS ist derzeit deaktiviert."
              className="w-full h-14 rounded-xl bg-zinc-800 border border-zinc-600 text-zinc-200 text-sm flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-90"
            >
              <CreditCard size={18} className="text-cyan-400" />
              <span className="font-semibold">Bankkarte</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-400 border border-zinc-600 rounded-full px-2 py-0.5">
                derzeit nicht verfügbar
              </span>
            </button>
            <div className="mt-5 text-[11px] text-zinc-500 leading-relaxed text-center">
              Der Verkauf wird erst nach erfolgreicher Zahlung verbucht.
              Bei Abbruch bleibt der Warenkorb erhalten.
            </div>
          </div>
        </div>
      )}

      {/* Phase C — Admin-Passwort-Modal zum Aktivieren des Lock Modes.
          Server prüft (Rolle admin + Passwort). Frontend verwaltet nur
          UI-State (localStorage-Flag). */}
      <AdminAuthModal
        open={lockModalOpen}
        title="POS Lock Mode aktivieren"
        description="Sidebar und Hauptnavigation werden ausgeblendet. Der Kassenrechner ist dann fokussiert auf den Verkauf. Verlassen benötigt erneut das Admin-Passwort."
        confirmLabel="Sperren"
        onClose={() => setLockModalOpen(false)}
        onConfirmed={() => { setLockModalOpen(false); activateLock(); }}
      />

      {/* Owner-Fix (05.09.2026) — Entsperren-Flow direkt neben dem
          KASSE-Header. Der frühere floating Button in Layout.jsx wurde
          entfernt; dieser Modal ist die einzige Verbleibende Unlock-UI. */}
      <AdminAuthModal
        open={unlockModalOpen}
        title="POS Lock Mode verlassen"
        description="Bitte Admin-Passwort eingeben, um den Kassenfokus-Modus zu beenden und die reguläre Navigation wieder freizugeben."
        confirmLabel="Entsperren"
        onClose={() => setUnlockModalOpen(false)}
        onConfirmed={() => { setUnlockModalOpen(false); releaseLock(); }}
      />

      {/* Phase C — Kundenkarten-Pflicht-Overlay.
          Sichtbar, wenn Setting = mandatory UND kein Kunde am Warenkorb
          UND keine Erfolgsanzeige aktiv. Blockiert die gesamte
          Kassenoberfläche echt (pointer-events auf dem Overlay
          absorbieren Klicks; darunter liegende Buttons sind nicht
          erreichbar). Der Karten-Input ist der EINZIGE fokussierbare
          Weg — ein Scan mit gültiger Karte hebt die Sperre auf.
          Backend erzwingt zusätzlich die Kundenzuordnung beim Checkout. */}
      {cardRequired && !customer && !receipt && (
        <div
          className="fixed inset-0 z-50 bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="pos-card-required-overlay"
          aria-modal="true"
          role="dialog"
        >
          <div className="w-full max-w-lg border border-cyan-500/40 bg-zinc-900 rounded-lg p-6 sm:p-10 text-center shadow-2xl">
            <div className="mx-auto w-16 h-16 rounded-full bg-cyan-500/15 text-cyan-400 flex items-center justify-center mb-4">
              <CreditCard size={30} />
            </div>
            <div className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">
              Bitte zuerst deine Kundenkarte scannen
            </div>
            <div className="text-sm text-zinc-400 mt-2 leading-relaxed">
              Halte deine {brandName} Club Karte an den Scanner
              — der Verkauf startet erst nach erfolgreicher Identifikation.
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); attachCustomer(); }}
              className="mt-6 space-y-3"
            >
              <Input
                autoFocus
                data-testid="pos-card-required-input"
                placeholder="Club-Card-ID scannen…"
                value={cardScan}
                onChange={(e) => setCardScan(e.target.value)}
                className="h-14 text-lg font-mono bg-zinc-950 border-zinc-700 focus-visible:ring-cyan-400 text-center"
              />
              <Button
                type="submit"
                data-testid="pos-card-required-submit"
                disabled={!cardScan.trim()}
                className="w-full h-12 bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider"
              >
                Karte anmelden
              </Button>
              <Button
                type="button"
                data-testid="pos-card-required-camera-btn"
                onClick={() => setCardCameraOpen(true)}
                variant="outline"
                className="w-full h-12 border-zinc-700 text-cyan-400 hover:text-cyan-300 hover:bg-zinc-800"
              >
                <QrCode size={18} className="mr-2" /> Karte scannen
              </Button>
            </form>
            <div className="text-[11px] text-zinc-500 mt-6 uppercase tracking-widest">
              Kundenkartenpflicht aktiv (Administrator-Einstellung)
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// POS Produkt-Suche (V1 Core Completion)
// ---------------------------------------------------------------------------
// Freitext-Suche (Name / EAN / interne ID) mit 300 ms Debounce, min 2 Zeichen.
// Nutzt exakt denselben Read-Pfad wie die Inventarliste
// (``GET /api/products?q=<term>&active=true``) — kein neuer Endpoint, kein
// N+1. Auswahl bucht per ``product_id`` über den regulären Cart-Endpoint,
// damit Cart-Lock und Recompute-Totals identisch greifen.
function PosProductSearchDialog({ open, onClose, onPick }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Reset beim (Wieder-)Öffnen — sonst zeigt der Dialog beim erneuten
  // Öffnen die Ergebnisse der Vor-Session.
  useEffect(() => {
    if (open) {
      setTerm("");
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Debounced Fetch. Kürzere Terms (< 2 Zeichen) räumen die Trefferliste
  // wieder ab, damit kein Stand von einer vorherigen Eingabe stehen bleibt.
  useEffect(() => {
    if (!open) return undefined;
    const q = term.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/products", { params: { q, active: true } });
        if (!cancelled) setResults(Array.isArray(r.data) ? r.data.slice(0, 20) : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [term, open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-start sm:items-center justify-center p-4 pt-16 sm:pt-4"
      data-testid="pos-product-search-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-bold uppercase tracking-wide">Produkt suchen</div>
          <Button size="icon" variant="ghost" onClick={onClose} data-testid="pos-product-search-close">
            <X size={18} />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-400" size={18} />
          <Input
            ref={inputRef}
            data-testid="pos-product-search-input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Name, EAN oder interne ID (min. 2 Zeichen)…"
            className="pl-10 h-12 bg-zinc-950 border-zinc-700 text-base focus-visible:ring-cyan-400"
          />
        </div>
        <div className="mt-3 min-h-[6rem] max-h-[55vh] overflow-y-auto" data-testid="pos-product-search-results">
          {term.trim().length < 2 && (
            <div className="text-xs text-zinc-500 py-4 text-center uppercase tracking-widest">
              Mindestens 2 Zeichen eingeben
            </div>
          )}
          {term.trim().length >= 2 && loading && (
            <div className="text-xs text-zinc-500 py-4 text-center">Suche…</div>
          )}
          {term.trim().length >= 2 && !loading && results.length === 0 && (
            <div
              data-testid="pos-product-search-empty"
              className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-800 rounded-sm"
            >
              Keine Treffer für „{term.trim()}"
            </div>
          )}
          <div className="space-y-1">
            {results.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`pos-product-search-item-${p.id}`}
                onClick={() => { onPick(p); onClose(); }}
                className="w-full text-left border border-zinc-700 hover:border-cyan-400 bg-zinc-800/60 hover:bg-zinc-700 rounded-sm px-3 py-3 flex items-center gap-3 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-100 truncate">{p.name}</div>
                  <div className="text-xs text-zinc-300 font-mono truncate">
                    {p.ean || p.internal_id || "—"}
                  </div>
                </div>
                <div className="font-mono font-bold text-cyan-400 whitespace-nowrap">
                  {formatCents(Math.round(Number(p.sales_price || 0) * 100))}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// POS Kategorie-Ansicht (Final UX Paket — 04.09.2026)
// ---------------------------------------------------------------------------
// Touch-Bedienung ohne Tastatur: Kategorien-Kacheln → Produkt-Kacheln →
// onPick(product) bucht per product_id. Datenquelle ist ausschließlich
// der bestehende Endpoint ``GET /api/products?active=true`` — kein neues
// Datenmodell, kein neuer Admin-Screen. Kategorien werden clientseitig
// aus dem ``category``-Feld aggregiert (typischer Bestand < 500 Produkte;
// ein Fetch reicht komplett aus). Wenn ein Produkt kein Bild hat, zeigen
// wir einen dezenten Platzhalter aus dem ersten Buchstaben.
function PosCategoryPicker({ open, onClose, onPick }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    if (!open) return;
    setActiveCategory(null);
    setLoading(true);
    api.get("/products", { params: { active: true } })
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [open]);

  const categories = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const key = (p.category || "").trim() || "Sonstiges";
      const list = map.get(key) || [];
      list.push(p);
      map.set(key, list);
    }
    // Alphabetisch, „Sonstiges" ans Ende.
    return Array.from(map.entries())
      .sort(([a], [b]) => {
        if (a === "Sonstiges") return 1;
        if (b === "Sonstiges") return -1;
        return a.localeCompare(b, "de");
      })
      .map(([name, items]) => ({ name, items }));
  }, [products]);

  if (!open) return null;
  const current = activeCategory
    ? categories.find((c) => c.name === activeCategory)
    : null;
  return (
    <div
      className="fixed inset-0 z-40 bg-black/80 flex items-start sm:items-center justify-center p-3 sm:p-6"
      data-testid="pos-category-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3 min-w-0">
            {current && (
              <button
                type="button"
                data-testid="pos-category-back-btn"
                onClick={() => setActiveCategory(null)}
                className="h-10 px-3 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs uppercase tracking-widest"
              >
                ← Kategorien
              </button>
            )}
            <div className="font-display text-base sm:text-lg font-black uppercase tracking-widest text-zinc-100 truncate">
              {current ? current.name : "Kategorien"}
            </div>
          </div>
          <button
            type="button"
            data-testid="pos-category-close-btn"
            onClick={onClose}
            className="h-10 w-10 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
            aria-label="Schließen"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {loading && (
            <div className="text-sm text-zinc-500 py-8 text-center uppercase tracking-widest">
              Lade Produkte …
            </div>
          )}
          {!loading && !current && categories.length === 0 && (
            <div className="text-sm text-zinc-500 py-10 text-center">
              Keine aktiven Produkte gefunden.
            </div>
          )}
          {/* Kategorie-Grid */}
          {!loading && !current && categories.length > 0 && (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
              data-testid="pos-category-grid"
            >
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  type="button"
                  data-testid={`pos-category-tile-${cat.name}`}
                  onClick={() => setActiveCategory(cat.name)}
                  className="aspect-square rounded-2xl bg-zinc-950 border border-zinc-800 hover:border-cyan-500 hover:bg-zinc-900 transition-colors flex flex-col items-center justify-center p-4 text-center"
                >
                  <div className="text-3xl font-black text-cyan-400 mb-2">
                    {cat.name[0]?.toUpperCase()}
                  </div>
                  <div className="font-semibold text-zinc-100 text-sm sm:text-base uppercase tracking-wide truncate w-full">
                    {cat.name}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {cat.items.length} Produkt{cat.items.length === 1 ? "" : "e"}
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* Produkt-Grid einer Kategorie */}
          {!loading && current && (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
              data-testid="pos-category-products"
            >
              {current.items.map((p) => {
                const priceCents = Math.round(Number(p.sales_price || 0) * 100);
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`pos-category-product-${p.id}`}
                    onClick={() => { onPick(p); }}
                    className="rounded-2xl bg-zinc-950 border border-zinc-800 hover:border-cyan-500 hover:bg-zinc-900 transition-colors overflow-hidden text-left flex flex-col"
                  >
                    <div className="aspect-square bg-zinc-800 flex items-center justify-center overflow-hidden">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name || "Produkt"} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-4xl font-black text-zinc-600">
                          {p.name?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="font-semibold text-zinc-100 text-sm truncate">{p.name}</div>
                      <div className="font-mono font-bold text-cyan-400 text-sm mt-1">
                        {formatCents(priceCents)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-zinc-800 px-5 py-3 text-[11px] text-zinc-500 leading-relaxed">
          Tippen Sie auf ein Produkt, um es in den Warenkorb zu legen.
          Für Preis-/Bestandsänderungen bitte weiterhin den Admin-Bereich nutzen.
        </div>
      </div>
    </div>
  );
}
